import { useRef, useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";
import LogoMark from "@/components/LogoMark";
import CertificateCanvas, { FRAME_OPTIONS } from "@/components/CertificateCanvas";
import GreetingSheet, { GREETING_DEFAULTS, GREET_BG_OPTIONS } from "@/components/GreetingCardCanvas";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CERT_DEFAULTS } from "@/components/Certificate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Trash2, Loader2, ImageIcon, Award, Save, Wand2, X, Pencil, Heart } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Settings() {
  const { logo, refresh } = useBranding();
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const inputRef = useRef(null);
  const frameRef = useRef(null);
  const greetBgRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [cert, setCert] = useState(CERT_DEFAULTS);
  const [savingCert, setSavingCert] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [newTplName, setNewTplName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [greeting, setGreeting] = useState(GREETING_DEFAULTS);
  const [savingGreet, setSavingGreet] = useState(false);
  const [greetTemplates, setGreetTemplates] = useState([]);
  const [newGreetTplName, setNewGreetTplName] = useState("");
  const [editingGreetId, setEditingGreetId] = useState(null);
  const [selectedGreetTpl, setSelectedGreetTpl] = useState("");
  const [greetBackgrounds, setGreetBackgrounds] = useState([]);
  const [certFrames, setCertFrames] = useState([]);

  useEffect(() => {
    api.get("/settings/certificate").then(({ data }) => setCert({ ...CERT_DEFAULTS, ...data })).catch(() => {});
    api.get("/settings/certificate-templates").then(({ data }) => setSavedTemplates(data.items || [])).catch(() => {});
    api.get("/settings/certificate-frames").then(({ data }) => setCertFrames(data.items || [])).catch(() => {});
    api.get("/settings/greeting").then(({ data }) => setGreeting({ ...GREETING_DEFAULTS, ...data })).catch(() => {});
    api.get("/settings/greeting-templates").then(({ data }) => setGreetTemplates(data.items || [])).catch(() => {});
    api.get("/settings/greeting-backgrounds").then(({ data }) => setGreetBackgrounds(data.items || [])).catch(() => {});
  }, []);

  const saveTemplate = async () => {
    const label = newTplName.trim();
    if (!label) return toast.error("Beri nama template terlebih dahulu");
    try {
      const { data } = await api.post("/settings/certificate-templates", { label, title: cert.title, body: cert.body });
      setSavedTemplates(data.items || []);
      setNewTplName("");
      toast.success("Template disimpan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteTemplate = async (tid) => {
    try {
      const { data } = await api.delete(`/settings/certificate-templates/${tid}`);
      setSavedTemplates(data.items || []);
      if (editingId === tid) { setEditingId(null); setNewTplName(""); }
      toast.success("Template dihapus");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const loadTemplateForEdit = (t) => {
    setCert((c) => ({ ...c, title: t.title, body: t.body }));
    setNewTplName(t.label);
    setEditingId(t.id);
  };

  const updateTemplate = async () => {
    const label = newTplName.trim();
    if (!label) return toast.error("Beri nama template terlebih dahulu");
    try {
      const { data } = await api.put(`/settings/certificate-templates/${editingId}`, { label, title: cert.title, body: cert.body });
      setSavedTemplates(data.items || []);
      setEditingId(null);
      setNewTplName("");
      toast.success("Template diperbarui");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const cancelEdit = () => { setEditingId(null); setNewTplName(""); };

  const onPick = () => inputRef.current?.click();

  const onFrameUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("File harus berupa gambar");
    if (file.size > 3 * 1024 * 1024) return toast.error("Ukuran gambar maksimal 3MB");
    const reader = new FileReader();
    reader.onload = async () => {
      const url = reader.result;
      setCert((c) => ({ ...c, frame: "custom", customFrame: url }));
      try {
        const label = `Bingkai Saya ${certFrames.length + 1}`;
        const { data } = await api.post("/settings/certificate-frames", { label, url });
        setCertFrames(data.items || []);
        toast.success("Bingkai diunggah & disimpan");
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };

  const deleteCertFrame = async (fid, url) => {
    try {
      const { data } = await api.delete(`/settings/certificate-frames/${fid}`);
      setCertFrames(data.items || []);
      if (cert.frame === "custom" && cert.customFrame === url) setCert((c) => ({ ...c, frame: "none", customFrame: null }));
      toast.success("Bingkai dihapus");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("File harus berupa gambar");
    if (file.size > 2 * 1024 * 1024) return toast.error("Ukuran gambar maksimal 2MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Logo berhasil diunggah");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    setBusy(true);
    try {
      await api.delete("/settings/logo");
      await refresh();
      toast.success("Logo dihapus");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const makeLogoTransparent = async () => {
    if (!logo) return;
    setProcessing(true);
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = logo; });
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = imgData.data;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i] > 235 && d[i + 1] > 235 && d[i + 2] > 235) d[i + 3] = 0;
      }
      ctx.putImageData(imgData, 0, 0);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const fd = new FormData();
      fd.append("file", new File([blob], "logo.png", { type: "image/png" }));
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Latar putih logo dihapus");
    } catch {
      toast.error("Gagal memproses logo");
    } finally {
      setProcessing(false);
    }
  };

  const saveCert = async () => {
    setSavingCert(true);
    try {
      await api.put("/settings/certificate", cert);
      toast.success("Pengaturan sertifikat disimpan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingCert(false);
    }
  };

  const previewData = {
    childName: "Gavin Nadial Givari",
    father: "Gilang Permana",
    mother: "Ulfa Agustin",
    eventDate: "2022-08-28",
    birth: "05 Agustus 2022",
    photoUrl: "",
  };

  const greetPreview = {
    childName: "Ghania Alesha",
    father: "Rian Candra Permana",
    mother: "Diani Septiani",
    photoUrl: "",
  };

  const onGreetBg = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("File harus berupa gambar");
    if (file.size > 3 * 1024 * 1024) return toast.error("Ukuran gambar maksimal 3MB");
    const reader = new FileReader();
    reader.onload = async () => {
      const url = reader.result;
      setGreeting((g) => ({ ...g, background: url }));
      try {
        const label = `Background Saya ${greetBackgrounds.length + 1}`;
        const { data } = await api.post("/settings/greeting-backgrounds", { label, url });
        setGreetBackgrounds(data.items || []);
        toast.success("Background diunggah & disimpan");
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };

  const deleteGreetBackground = async (bid, url) => {
    try {
      const { data } = await api.delete(`/settings/greeting-backgrounds/${bid}`);
      setGreetBackgrounds(data.items || []);
      if (greeting.background === url) setGreeting((g) => ({ ...g, background: GREETING_DEFAULTS.background }));
      toast.success("Background dihapus");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveGreeting = async () => {
    setSavingGreet(true);
    try {
      await api.put("/settings/greeting", greeting);
      toast.success("Pengaturan kartu ucapan disimpan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingGreet(false);
    }
  };

  const saveGreetTemplate = async () => {
    const label = newGreetTplName.trim();
    if (!label) return toast.error("Beri nama template terlebih dahulu");
    try {
      const { data } = await api.post("/settings/greeting-templates", { label, intro: greeting.intro, doa: greeting.doa });
      setGreetTemplates(data.items || []);
      setNewGreetTplName("");
      toast.success("Template kartu disimpan");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const updateGreetTemplate = async () => {
    const label = newGreetTplName.trim();
    if (!label) return toast.error("Beri nama template terlebih dahulu");
    try {
      const { data } = await api.put(`/settings/greeting-templates/${editingGreetId}`, { label, intro: greeting.intro, doa: greeting.doa });
      setGreetTemplates(data.items || []);
      setEditingGreetId(null);
      setNewGreetTplName("");
      toast.success("Template kartu diperbarui");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteGreetTemplate = async (tid) => {
    try {
      const { data } = await api.delete(`/settings/greeting-templates/${tid}`);
      setGreetTemplates(data.items || []);
      if (editingGreetId === tid) { setEditingGreetId(null); setNewGreetTplName(""); }
      toast.success("Template kartu dihapus");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const loadGreetForEdit = (t) => {
    setGreeting((g) => ({ ...g, intro: t.intro, doa: t.doa }));
    setNewGreetTplName(t.label);
    setEditingGreetId(t.id);
  };

  const cancelGreetEdit = () => { setEditingGreetId(null); setNewGreetTplName(""); };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8 animate-fade-up">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-gold">Konfigurasi</p>
        <h1 className="mt-1 font-serif text-4xl font-bold text-brand-espresso">Pengaturan</h1>
      </div>

      {/* Logo */}
      {!isStaff && (
      <div className="max-w-2xl rounded-2xl border border-brand-line bg-white p-8 shadow-[0_8px_30px_rgba(45,35,30,0.05)]">
        <h2 className="font-serif text-2xl font-bold text-brand-espresso">Logo Brand</h2>
        <p className="mt-1 text-sm text-brand-muted">
          Logo akan tampil di sidebar, halaman masuk, nota pesanan, dan sertifikat. Format gambar (PNG/JPG/SVG), maksimal 2MB.
        </p>
        <div className="mt-6 flex flex-col items-start gap-6 sm:flex-row sm:items-center">
          <div className="flex h-28 w-28 items-center justify-center rounded-2xl border border-brand-line bg-brand-surface p-2">
            {logo ? <LogoMark size={96} className="shadow-none" /> : (
              <div className="flex flex-col items-center gap-1 text-brand-muted">
                <ImageIcon className="h-6 w-6" /><span className="text-[11px]">Belum ada</span>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              <Button onClick={onPick} disabled={busy} data-testid="upload-logo-btn"
                className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover transition-colors duration-200">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {logo ? "Ganti Logo" : "Unggah Logo"}
              </Button>
              {logo && (
                <Button onClick={makeLogoTransparent} disabled={processing} variant="outline" data-testid="logo-transparent-btn"
                  className="gap-2 border-brand-line">
                  {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Jadikan Transparan
                </Button>
              )}
              {logo && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" disabled={busy} data-testid="remove-logo-btn"
                      className="gap-2 border-brand-line text-destructive hover:bg-red-50">
                      <Trash2 className="h-4 w-4" /> Hapus
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Hapus logo?</AlertDialogTitle>
                      <AlertDialogDescription>Tampilan akan kembali ke marka "R".</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Batal</AlertDialogCancel>
                      <AlertDialogAction onClick={onRemove} className="bg-destructive hover:bg-destructive/90">Hapus</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
            <p className="text-xs text-brand-muted">Disarankan gambar persegi (mis. 512×512) dengan latar transparan.</p>
          </div>
        </div>
        <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="logo-file-input" />
      </div>
      )}

      {/* Certificate */}
      <div className="mt-8 max-w-2xl rounded-2xl border border-brand-line bg-white p-8 shadow-[0_8px_30px_rgba(45,35,30,0.05)]" data-testid="cert-settings">
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5 text-brand-gold" />
          <h2 className="font-serif text-2xl font-bold text-brand-espresso">Sertifikat Aqiqah</h2>
        </div>
        <p className="mt-1 text-sm text-brand-muted">
          Atur teks dan informasi kontak yang tampil di sertifikat. Foto anak, nama, tanggal, dan nama orang tua otomatis diambil dari data pesanan.
        </p>

        {/* Frame theme */}
        <div className="mt-6">
          <Label>Tema Bingkai</Label>
          <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {FRAME_OPTIONS.map((f) => (
              <button key={f.id} type="button" onClick={() => setCert((c) => ({ ...c, frame: f.id }))} data-testid={`cert-frame-${f.id}`}
                className={`overflow-hidden rounded-lg border-2 transition ${cert.frame === f.id ? "border-brand-gold ring-2 ring-brand-gold/40" : "border-brand-line hover:border-brand-gold/60"}`}>
                <div className="flex h-24 items-center justify-center bg-brand-surface">
                  {f.url ? (
                    <img src={f.url} alt={f.label} className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-xs font-medium text-brand-muted">Tanpa Bingkai</span>
                  )}
                </div>
                <div className="bg-white py-1.5 text-center text-xs font-semibold text-brand-espresso">{f.label}</div>
              </button>
            ))}
            {certFrames.map((f) => (
              <div key={f.id} className="relative">
                <button type="button" onClick={() => setCert((c) => ({ ...c, frame: "custom", customFrame: f.url }))} data-testid={`cert-frame-saved-${f.id}`}
                  className={`w-full overflow-hidden rounded-lg border-2 transition ${cert.frame === "custom" && cert.customFrame === f.url ? "border-brand-gold ring-2 ring-brand-gold/40" : "border-brand-line hover:border-brand-gold/60"}`}>
                  <div className="flex h-24 items-center justify-center bg-brand-surface">
                    <img src={f.url} alt={f.label} className="h-full w-full object-cover" />
                  </div>
                  <div className="bg-white py-1.5 text-center text-xs font-semibold text-brand-espresso">{f.label}</div>
                </button>
                <button type="button" onClick={() => deleteCertFrame(f.id, f.url)} data-testid={`del-cert-frame-${f.id}`}
                  className="absolute right-1.5 top-1.5 rounded-full bg-white/90 p-1 text-brand-muted shadow hover:text-destructive">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => frameRef.current?.click()} data-testid="cert-frame-upload"
              className="flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-brand-line py-4 text-brand-muted transition hover:border-brand-gold hover:text-brand-espresso">
              <Upload className="h-5 w-5" />
              <span className="text-xs font-semibold">Unggah Bingkai</span>
            </button>
          </div>
          <input ref={frameRef} type="file" accept="image/*" onChange={onFrameUpload} className="hidden" data-testid="cert-frame-input" />
          <p className="mt-2 text-xs text-brand-muted">Unggah desain bingkai sendiri (disarankan rasio 10R potrait, area tengah kosong untuk teks). Maksimal 3MB.</p>
        </div>

        {/* Show photo toggle */}
        <div className="mt-6 flex items-center justify-between rounded-lg border border-brand-line p-4">
          <div>
            <Label>Tampilkan Foto Anak</Label>
            <p className="mt-0.5 text-xs text-brand-muted">Nonaktifkan untuk membuat sertifikat tanpa foto anak.</p>
          </div>
          <Switch checked={cert.showPhoto} onCheckedChange={(v) => setCert((c) => ({ ...c, showPhoto: v }))} data-testid="cert-showphoto-switch" />
        </div>

        {/* Text fields */}
        <div className="mt-6 space-y-4">
          <div>
            <Label>Template Teks</Label>
            <Select value={selectedTpl} onValueChange={(v) => { setSelectedTpl(v); const t = savedTemplates.find((x) => x.id === v); if (t) setCert((c) => ({ ...c, title: t.title, body: t.body })); }}>
              <SelectTrigger data-testid="cert-template-select" className="mt-1.5 focus:ring-brand-gold"><SelectValue placeholder="Pilih template tersimpan..." /></SelectTrigger>
              <SelectContent>
                {savedTemplates.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-brand-muted">Belum ada template. Simpan template di bawah.</div>
                ) : savedTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {savedTemplates.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {savedTemplates.map((t) => (
                  <span key={t.id} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${editingId === t.id ? "bg-brand-gold/20 text-brand-espresso ring-1 ring-brand-gold" : "bg-brand-surface text-brand-espresso"}`} data-testid={`saved-tpl-${t.id}`}>
                    {t.label}
                    <button type="button" onClick={() => loadTemplateForEdit(t)} className="text-brand-muted hover:text-brand-gold" data-testid={`edit-tpl-${t.id}`}><Pencil className="h-3 w-3" /></button>
                    <button type="button" onClick={() => deleteTemplate(t.id)} className="text-brand-muted hover:text-destructive" data-testid={`del-tpl-${t.id}`}><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="mt-2 flex gap-2">
              <Input value={newTplName} onChange={(e) => setNewTplName(e.target.value)} placeholder={editingId ? "Nama template" : "Nama template baru"} data-testid="tpl-name-input" className="focus-visible:ring-brand-gold" />
              <Button type="button" variant="outline" onClick={editingId ? updateTemplate : saveTemplate} data-testid="save-tpl-btn" className="shrink-0 gap-2 border-brand-line">
                <Save className="h-4 w-4" /> {editingId ? "Perbarui" : "Simpan Template"}
              </Button>
              {editingId && (
                <Button type="button" variant="ghost" onClick={cancelEdit} data-testid="cancel-tpl-btn" className="shrink-0 text-brand-muted">Batal</Button>
              )}
            </div>
          </div>
          <div>
            <Label>Judul Sertifikat</Label>
            <Input value={cert.title} onChange={(e) => setCert((c) => ({ ...c, title: e.target.value }))} data-testid="cert-title-input" className="mt-1.5 focus-visible:ring-brand-gold" />
          </div>
          <div>
            <Label>Paragraf Isi (Doa &amp; Ucapan)</Label>
            <Textarea rows={5} value={cert.body} onChange={(e) => setCert((c) => ({ ...c, body: e.target.value }))} data-testid="cert-body-input" className="mt-1.5 focus-visible:ring-brand-gold" />
          </div>
          <div>
            <Label>Alamat (Footer)</Label>
            <Textarea rows={2} value={cert.address} onChange={(e) => setCert((c) => ({ ...c, address: e.target.value }))} data-testid="cert-address-input" className="mt-1.5 focus-visible:ring-brand-gold" />
          </div>
          <div>
            <Label>Nomor Telp / WA (Footer)</Label>
            <Input value={cert.phone} onChange={(e) => setCert((c) => ({ ...c, phone: e.target.value }))} data-testid="cert-phone-input" className="mt-1.5 focus-visible:ring-brand-gold" />
          </div>
        </div>

        {/* Live preview */}
        <div className="mt-6">
          <Label>Pratinjau Langsung</Label>
          <div className="mt-2 flex justify-center rounded-xl border border-brand-line bg-brand-surface p-4">
            <div style={{ width: 291, height: 350 }}>
              <div style={{ transform: "scale(0.35)", transformOrigin: "top left" }} data-testid="cert-preview">
                <CertificateCanvas id="cert-preview-canvas" cfg={cert} data={previewData} />
              </div>
            </div>
          </div>
        </div>

        <Button onClick={saveCert} disabled={savingCert} data-testid="cert-save-btn"
          className="mt-6 gap-2 bg-brand-espresso text-white hover:bg-brand-espresso/90">
          {savingCert ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan Sertifikat
        </Button>
      </div>

      {/* Kartu Ucapan */}
      <div className="mt-8 max-w-2xl rounded-2xl border border-brand-line bg-white p-8 shadow-[0_8px_30px_rgba(45,35,30,0.05)]" data-testid="greeting-settings">
        <div className="flex items-center gap-2">
          <Heart className="h-5 w-5 text-brand-gold" />
          <h2 className="font-serif text-2xl font-bold text-brand-espresso">Kartu Ucapan</h2>
        </div>
        <p className="mt-1 text-sm text-brand-muted">
          Kartu ucapan dicetak dalam 1 lembar A4 berisi 4 kartu (2×2) dengan ukuran sama. Nama anak & nama orang tua otomatis dari data pesanan.
        </p>

        {/* Background */}
        <div className="mt-6">
          <Label>Background Kartu</Label>
          <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {GREET_BG_OPTIONS.map((o) => (
              <button key={o.id} type="button" onClick={() => setGreeting((g) => ({ ...g, background: o.url }))} data-testid={`greet-bg-${o.id}`}
                className={`overflow-hidden rounded-lg border-2 transition ${greeting.background === o.url ? "border-brand-gold ring-2 ring-brand-gold/40" : "border-brand-line hover:border-brand-gold/60"}`}>
                <div className="flex h-20 items-center justify-center bg-brand-surface">
                  {o.url ? <img src={o.url} alt={o.label} className="h-full w-full object-cover" /> : <span className="text-xs font-medium text-brand-muted">Polos</span>}
                </div>
                <div className="bg-white py-1.5 text-center text-xs font-semibold text-brand-espresso">{o.label}</div>
              </button>
            ))}
            {greetBackgrounds.map((o) => (
              <div key={o.id} className="relative">
                <button type="button" onClick={() => setGreeting((g) => ({ ...g, background: o.url }))} data-testid={`greet-bg-saved-${o.id}`}
                  className={`w-full overflow-hidden rounded-lg border-2 transition ${greeting.background === o.url ? "border-brand-gold ring-2 ring-brand-gold/40" : "border-brand-line hover:border-brand-gold/60"}`}>
                  <div className="h-20 bg-brand-surface"><img src={o.url} alt={o.label} className="h-full w-full object-cover" /></div>
                  <div className="bg-white py-1.5 text-center text-xs font-semibold text-brand-espresso">{o.label}</div>
                </button>
                <button type="button" onClick={() => deleteGreetBackground(o.id, o.url)} data-testid={`del-greet-bg-${o.id}`}
                  className="absolute right-1.5 top-1.5 rounded-full bg-white/90 p-1 text-brand-muted shadow hover:text-destructive">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => greetBgRef.current?.click()} data-testid="greet-bg-upload"
              className="flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-brand-line py-4 text-brand-muted transition hover:border-brand-gold hover:text-brand-espresso">
              <Upload className="h-5 w-5" />
              <span className="text-xs font-semibold">Unggah Background</span>
            </button>
          </div>
          <input ref={greetBgRef} type="file" accept="image/*" onChange={onGreetBg} className="hidden" data-testid="greet-bg-input" />
          <p className="mt-2 text-xs text-brand-muted">Unggah background kartu (disarankan rasio landscape ~1.4, area tengah kosong untuk teks). Maksimal 3MB.</p>
        </div>

        {/* Show photo */}
        <div className="mt-6 flex items-center justify-between rounded-lg border border-brand-line p-4">
          <div>
            <Label>Tampilkan Foto Anak</Label>
            <p className="mt-0.5 text-xs text-brand-muted">Nonaktifkan untuk kartu ucapan tanpa foto.</p>
          </div>
          <Switch checked={greeting.showPhoto} onCheckedChange={(v) => setGreeting((g) => ({ ...g, showPhoto: v }))} data-testid="greet-showphoto-switch" />
        </div>

        {/* Text fields */}
        <div className="mt-6 space-y-4">
          <div>
            <Label>Template Pengantar &amp; Doa</Label>
            <Select value={selectedGreetTpl} onValueChange={(v) => { setSelectedGreetTpl(v); const t = greetTemplates.find((x) => x.id === v); if (t) setGreeting((g) => ({ ...g, intro: t.intro, doa: t.doa })); }}>
              <SelectTrigger data-testid="greet-template-select" className="mt-1.5 focus:ring-brand-gold"><SelectValue placeholder="Pilih template tersimpan..." /></SelectTrigger>
              <SelectContent>
                {greetTemplates.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-brand-muted">Belum ada template. Simpan template di bawah.</div>
                ) : greetTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {greetTemplates.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {greetTemplates.map((t) => (
                  <span key={t.id} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${editingGreetId === t.id ? "bg-brand-gold/20 text-brand-espresso ring-1 ring-brand-gold" : "bg-brand-surface text-brand-espresso"}`} data-testid={`greet-tpl-${t.id}`}>
                    {t.label}
                    <button type="button" onClick={() => loadGreetForEdit(t)} className="text-brand-muted hover:text-brand-gold" data-testid={`edit-greet-tpl-${t.id}`}><Pencil className="h-3 w-3" /></button>
                    <button type="button" onClick={() => deleteGreetTemplate(t.id)} className="text-brand-muted hover:text-destructive" data-testid={`del-greet-tpl-${t.id}`}><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="mt-2 flex gap-2">
              <Input value={newGreetTplName} onChange={(e) => setNewGreetTplName(e.target.value)} placeholder={editingGreetId ? "Nama template" : "Nama template baru"} data-testid="greet-tpl-name-input" className="focus-visible:ring-brand-gold" />
              <Button type="button" variant="outline" onClick={editingGreetId ? updateGreetTemplate : saveGreetTemplate} data-testid="save-greet-tpl-btn" className="shrink-0 gap-2 border-brand-line">
                <Save className="h-4 w-4" /> {editingGreetId ? "Perbarui" : "Simpan Template"}
              </Button>
              {editingGreetId && (
                <Button type="button" variant="ghost" onClick={cancelGreetEdit} data-testid="cancel-greet-tpl-btn" className="shrink-0 text-brand-muted">Batal</Button>
              )}
            </div>
          </div>
          <div>
            <Label>Teks Pembuka</Label>
            <Input value={greeting.bismillah} onChange={(e) => setGreeting((g) => ({ ...g, bismillah: e.target.value }))} data-testid="greet-bismillah-input" className="mt-1.5 focus-visible:ring-brand-gold" />
          </div>
          <div>
            <Label>Paragraf Pengantar</Label>
            <Textarea rows={3} value={greeting.intro} onChange={(e) => setGreeting((g) => ({ ...g, intro: e.target.value }))} data-testid="greet-intro-input" className="mt-1.5 focus-visible:ring-brand-gold" />
          </div>
          <div>
            <Label>Doa &amp; Ucapan</Label>
            <Textarea rows={4} value={greeting.doa} onChange={(e) => setGreeting((g) => ({ ...g, doa: e.target.value }))} data-testid="greet-doa-input" className="mt-1.5 focus-visible:ring-brand-gold" />
          </div>
        </div>

        {/* Preview (single card) */}
        <div className="mt-6">
          <Label>Pratinjau Kartu</Label>
          <div className="mt-2 flex justify-center rounded-xl border border-brand-line bg-brand-surface p-4">
            <div style={{ width: 375, height: 265 }}>
              <div style={{ transform: "scale(0.33)", transformOrigin: "top left" }} data-testid="greet-preview">
                <GreetingSheet id="greet-preview-sheet" cfg={greeting} data={greetPreview} />
              </div>
            </div>
          </div>
          <p className="mt-2 text-xs text-brand-muted">Pratinjau lembar A4 (4 kartu). Saat dibuka dari pesanan, nama otomatis mengikuti data.</p>
        </div>

        <Button onClick={saveGreeting} disabled={savingGreet} data-testid="greet-save-btn"
          className="mt-6 gap-2 bg-brand-espresso text-white hover:bg-brand-espresso/90">
          {savingGreet ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan Kartu Ucapan
        </Button>
      </div>
    </div>
  );
}
